import { useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { subscriptionApi, type OpenPositionRow } from "@/api/client";
import { CLOSE_PERCENTS, closeIntentFor, closeOutcome, closeResultFor, subscriptionsToClose } from "@/lib/closePosition";
import { money, size as fmtSize } from "@/lib/openTrades";
import { useAssetName } from '@/lib/instruments';

/****************
 * CLOSING A POSITION FROM THE TRAY.
 *
 * One contract can be held by more than one session. The venue shows a single merged
 * position and cannot tell the legs apart, so closing is only expressible per session —
 * which makes Close a question rather than an action whenever a contract is shared:
 * whose leg, or everyone's.
 *
 * A shared contract therefore never closes on one click. Closing one session's leg when
 * all were meant leaves money on the exchange, which the user can see and fix; closing
 * every session when one was meant takes a position they wanted to keep, which they
 * cannot undo. The asymmetry is why the question is always asked.
 *
 * Each leg is closed by its own call. There is no merged close on the server and there
 * should not be: it would have to guess the split, and the pro-rata split exists because
 * guessing it is wrong.
 ****************/

export function ClosePositionDialog({
  row,
  onClose,
  onClosed,
  paper = false,
}: {
  row: OpenPositionRow | null;
  onClose: () => void;
  onClosed: () => void;
  /** A paper position: closed at the current price, nothing sent anywhere (M-99). */
  paper?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  // How much to close (Guillaume, 2026-10-14): any percent, 100 = all of it.
  const [pct, setPct] = useState(100);
  const { t } = useTranslation("copy");
  const an = useAssetName();

  if (!row) return null;
  const intent = closeIntentFor(row.sessions);
  if (intent.kind === "none") return null;

  const run = async (choice: "all" | number) => {
    const targets =
      intent.kind === "choose"
        ? subscriptionsToClose(intent.sessions, choice)
        : [intent.subscriptionId];
    if (targets.length === 0) return;

    setBusy(true);
    /*
     * Sequential, not parallel. Each call places a market order and waits for its fill;
     * firing them together at one account is how two closes race on the same contract.
     */
    /*
     * `unknown` belongs on this type, and leaving it off was not cosmetic:
     * TypeScript rejected the push, so the one field carrying "we could not say
     * whether it closed" never reached closeOutcome and the UI fell back to the
     * flat failure message. The distinction this change exists to make did not
     * survive the boundary.
     */
    const results: Array<{
      subscriptionId: number;
      ok: boolean;
      error?: string;
      /** True when nothing readable came back from OUR server — see closeOutcome. */
      unknown?: boolean;
    }> = [];
    for (const subscriptionId of targets) {
      try {
        await subscriptionApi.closePosition(subscriptionId, row.symbol, row.side, pct);
        results.push({ subscriptionId, ok: true });
      } catch (e: any) {
        // The mapping lives in lib so it can be tested — see closeResultFor.
        results.push(closeResultFor(subscriptionId, e));
      }
    }
    setBusy(false);

    const outcome = closeOutcome(results);
    if (outcome.ok) toast.success(outcome.message);
    else toast.error(outcome.message, { duration: 10000 });

    /*
     * CLOSE THE DIALOG FIRST, REFRESH AFTER. 2026-09-22.
     *
     * Guillaume: "When I close a position or cancel an order on the Open Trade
     * Trail, it makes a massive random black square appear in my screen and I
     * need to refresh to make him go." onClosed() refetches, which re-renders
     * the tray and can unmount this dialog WHILE it is still closing -- Radix
     * then never finishes its teardown, so the overlay stays on the page and
     * keeps swallowing clicks. Unmount first, refresh on the next tick.
     */
    onClose();
    setTimeout(onClosed, 0);
  };

  const what = `${an(row.symbol)} ${row.side}`;

  return (
    <AlertDialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {intent.kind === "choose" ? t("close.title.choose", { what }) : t("close.title.single", { what })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {paper ? (
              <>
                {t("close.body.paper", { symbol: an(row.symbol) })}
              </>
            ) : intent.kind === "choose" ? (
              <>
                {t("close.body.choose", { count: intent.sessions.length, symbol: an(row.symbol) })}
              </>
            ) : (
              <>
                {t("close.body.single", { symbol: an(row.symbol) })}
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2">
          <div className="text-xs font-medium text-muted-foreground">{t("close.pct.label")}</div>
          <div className="flex flex-wrap items-center gap-2">
            {CLOSE_PERCENTS.map((p) => (
              <Button
                key={p}
                type="button"
                size="sm"
                variant={pct === p ? "default" : "outline"}
                disabled={busy}
                onClick={() => setPct(p)}
              >
                {p}%
              </Button>
            ))}
            <div className="flex items-center gap-1">
              <input
                type="number"
                min={1}
                max={100}
                step={1}
                inputMode="decimal"
                aria-label={t("close.pct.custom")}
                className="h-8 w-20 rounded-md border border-border bg-background px-2 text-sm tabular-nums"
                value={pct}
                disabled={busy}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (Number.isFinite(v)) setPct(Math.min(100, Math.max(1, v)));
                }}
              />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
          </div>
        </div>

        {intent.kind === "choose" && (
          <div className="space-y-2">
            {intent.sessions.map((s) => (
              <div
                key={s.subscription_id}
                className="flex items-center justify-between gap-3 rounded-md border border-border/60 p-2.5"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {s.trader_name || t("close.sessionFallback", { id: s.subscription_id })}
                  </div>
                  <div className="text-[11px] text-muted-foreground tabular-nums">
                    #{s.subscription_id} · {fmtSize(s.size ?? 0)}
                    {Number.isFinite(s.unrealized_pnl) ? ` · ${money(s.unrealized_pnl as number)}` : ""}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => run(s.subscription_id)}
                >
                  {pct < 100 ? t("close.pct.closeThis", { pct }) : t("close.closeThis")}
                </Button>
              </div>
            ))}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>{t("action.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            onClick={(e) => {
              // The dialog would otherwise close on click, before the closes have been
              // placed, and the user would never see which of them failed.
              e.preventDefault();
              void run("all");
            }}
            className="bg-danger hover:bg-danger/90"
          >
            {busy
              ? t("close.closing")
              : pct < 100
                ? t("close.pct.close", { pct })
                : intent.kind === "choose"
                  ? t("close.closeAll", { count: intent.sessions.length })
                  : t("close.closePosition")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
